import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService } from '@progress/kendo-angular-l10n';
import { TranslateService } from '@ngx-translate/core';

/** Maps Kendo's `kendo.grid.*` message keys (see `GridMessages` in `@progress/kendo-angular-grid`)
 *  to this app's translation keys — the pager text and the groupable group-panel placeholder are
 *  covered today. Empty-state messages are localized per-consumer via each grid's own
 *  `kendoGridNoRecordsTemplate` override instead, so `noRecords` is intentionally left unmapped
 *  here. */
const KENDO_GRID_MESSAGE_KEYS: Record<string, string> = {
  'kendo.grid.pagerFirstPage': 'SHARED.KENDO_GRID.PAGER_FIRST_PAGE',
  'kendo.grid.pagerPreviousPage': 'SHARED.KENDO_GRID.PAGER_PREVIOUS_PAGE',
  'kendo.grid.pagerNextPage': 'SHARED.KENDO_GRID.PAGER_NEXT_PAGE',
  'kendo.grid.pagerLastPage': 'SHARED.KENDO_GRID.PAGER_LAST_PAGE',
  'kendo.grid.pagerPage': 'SHARED.KENDO_GRID.PAGER_PAGE',
  'kendo.grid.pagerItemsPerPage': 'SHARED.KENDO_GRID.PAGER_ITEMS_PER_PAGE',
  'kendo.grid.pagerOf': 'SHARED.KENDO_GRID.PAGER_OF',
  'kendo.grid.pagerItems': 'SHARED.KENDO_GRID.PAGER_ITEMS',
  'kendo.grid.pagerPageNumberInputTitle': 'SHARED.KENDO_GRID.PAGER_PAGE_NUMBER_INPUT_TITLE',
  'kendo.grid.pagerInputLabel': 'SHARED.KENDO_GRID.PAGER_INPUT_LABEL',
  'kendo.grid.pagerSelectPage': 'SHARED.KENDO_GRID.PAGER_SELECT_PAGE',
  'kendo.grid.groupPanelEmpty': 'SHARED.KENDO_GRID.GROUP_PANEL_EMPTY',
};

/**
 * Translates Kendo Grid's built-in pager text via this app's i18n instead of Kendo's shipped
 * English defaults. Provided app-wide in `app.config.ts` (`{ provide: MessageService, useClass:
 * KendoGridMessagesService }`) so every `<kendo-grid>`/`<app-configurable-grid>` picks it up
 * without each consumer wiring its own provider. Kendo caches messages until `notify()` fires, so
 * a language switch re-pushes them — see `LocalizationService`'s `changes` subscription in
 * `@progress/kendo-angular-l10n`.
 */
@Injectable()
export class KendoGridMessagesService extends MessageService {
  private readonly translate = inject(TranslateService);

  constructor() {
    super();
    // Optional chaining: many existing component specs across the app mount a grid behind a
    // lightweight `TranslateService` test double that only stubs `instant`/`translate` (no
    // `onLangChange`) — this keeps this service safe to construct against those doubles instead
    // of requiring every one of them to be updated for an unrelated feature.
    this.translate.onLangChange?.pipe(takeUntilDestroyed()).subscribe(() => this.notify());
  }

  override get(key: string): string | undefined {
    const i18nKey = KENDO_GRID_MESSAGE_KEYS[key];
    return i18nKey ? this.translate.instant(i18nKey) : undefined;
  }
}
